import { memo } from 'react'

import {
  DerivationVariant,
  FileItem,
  Item,
  ItemType,
  ShortcutItem
} from '@inno-sdks/files'
import { BasicFileIconProps, FolderBlue, Other } from '@inno-ui/file-icons'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { getSystemShortcutDefinition } from 'modules/desktop/modules/files-integration/constants'
import { getShortcutInfo } from 'modules/desktop/modules/files-integration/utils'

import styles from './ItemIcon.module.scss'
import { getFileIcon } from './helpers'

type ItemIconProps = {
  item: Item
  size?: BasicFileIconProps['size']
  /**
   * @default false
   */
  showThumbnail?: boolean
}

export const ItemIcon = memo<ItemIconProps>(
  ({ item, size, showThumbnail = false }) => {
    const { apps } = useAvailableApps()

    if (item.type === ItemType.Folder) {
      return <FolderBlue size={size} className='itemIcon' />
    }

    if (item.type === ItemType.Shortcut) {
      const systemShortcut = getSystemShortcutDefinition(item)
      if (systemShortcut?.presentation.iconType === 'image') {
        const iconApp = systemShortcut.iconAppId
          ? apps[systemShortcut.iconAppId]
          : undefined
        const appIcon =
          iconApp?.appIcon ?? iconApp?.icon ?? systemShortcut.presentation.icon

        return (
          <img
            src={appIcon}
            className='itemIcon'
            width={size}
            height={size}
            alt={item.name}
          />
        )
      }

      const { shortcutUrl } = item.metaData
      const { isInternal, appIcon } = getShortcutInfo(shortcutUrl, apps)

      if (isInternal && appIcon) {
        return (
          <img
            src={appIcon}
            className='itemIcon'
            width={size}
            height={size}
            alt={item.name}
          />
        )
      }

      return <Other size={size} className='itemIcon' />
    }

    if (showThumbnail) {
      const thumbnail = item.metaData.derivations?.find(
        ({ variant }) => variant === DerivationVariant.Small
      )?.signedUrl

      if (thumbnail) {
        return (
          <div className={styles.thumbnailWrapper}>
            <img src={thumbnail} alt={item.name} className={styles.thumbnail} />
          </div>
        )
      }
    }

    const FileIcon = getFileIcon(item.metaData)
    return <FileIcon size={size} className='itemIcon' />
  },
  (prev, next) => {
    if (next.item.type === ItemType.Folder) {
      return true
    }

    if (next.item.type === ItemType.Shortcut) {
      return (
        (prev.item as ShortcutItem).metaData.shortcutUrl ===
        (next.item as ShortcutItem).metaData.shortcutUrl
      )
    }

    return (
      (prev.item as FileItem).metaData.derivations?.length ===
      (next.item as FileItem).metaData.derivations?.length
    )
  }
)
